import { useEffect, useState, useMemo } from 'react';
import { api } from '../utils/api';
import Spinner from '../components/Spinner';

const BASE_URL = '/api';

function getToken() {
  return localStorage.getItem('ozon_token');
}

async function apiFetch(path, options = {}) {
  const token = getToken();
  const res = await fetch(`${BASE_URL}${path}`, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    ...options,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Hata');
  return data;
}

// Fiyat geçmişi mini grafiği
function PriceChart({ history }) {
  if (!history.length) return null;
  const prices = history.map((h) => parseFloat(h.purchase_price));
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const range = max - min || 1;

  return (
    <div className="flex items-end gap-1 h-10">
      {history.slice(0, 12).reverse().map((h, i) => {
        const pct = ((parseFloat(h.purchase_price) - min) / range) * 100;
        const height = Math.max(15, pct);
        return (
          <div key={i} className="flex flex-col items-center gap-0.5 group relative">
            <div
              className="w-4 bg-blue-400 rounded-t-sm transition-all group-hover:bg-blue-600"
              style={{ height: `${height}%` }}
              title={`${h.purchase_price} ${h.currency} — ${new Date(h.created_at).toLocaleDateString('tr-TR')}`}
            />
          </div>
        );
      })}
    </div>
  );
}

// ─── Form ────────────────────────────────────────────────────────────────────

function EntryForm({ products, locations, onSaved }) {
  const [form, setForm] = useState({
    offer_id: '',
    quantity: '',
    purchase_price: '',
    currency: 'TRY',
    warehouse_location: '',
    supplier: '',
    note: '',
  });
  const [productSearch, setProductSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const [priceHistory, setPriceHistory] = useState([]);

  const selected = products.find((p) => p.offer_id === form.offer_id);

  // Ürün seçince fiyat geçmişini çek
  useEffect(() => {
    if (!form.offer_id) { setPriceHistory([]); return; }
    apiFetch(`/stock-entries/price-history/${encodeURIComponent(form.offer_id)}`)
      .then(setPriceHistory)
      .catch(() => {});
  }, [form.offer_id]);

  const filteredProducts = useMemo(() => {
    if (!productSearch) return products.slice(0, 40);
    const q = productSearch.toLowerCase();
    return products.filter(
      (p) =>
        (p.name || '').toLowerCase().includes(q) ||
        (p.offer_id || '').toLowerCase().includes(q) ||
        String(p.sku || '').includes(q)
    ).slice(0, 40);
  }, [products, productSearch]);

  function set(key, val) {
    setForm((prev) => ({ ...prev, [key]: val }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSuccess(null);
    setSaving(true);
    try {
      const result = await apiFetch('/stock-entries', {
        method: 'POST',
        body: JSON.stringify({
          ...form,
          quantity: parseInt(form.quantity, 10),
          purchase_price: parseFloat(form.purchase_price),
        }),
      });
      setSuccess({ product: selected?.name, qty: form.quantity, newStock: result.new_stock });
      onSaved();
      // Formu kısmen sıfırla
      setForm((prev) => ({ ...prev, quantity: '', purchase_price: '', note: '' }));
      // Yeni fiyat geçmişini yenile
      apiFetch(`/stock-entries/price-history/${encodeURIComponent(form.offer_id)}`)
        .then(setPriceHistory)
        .catch(() => {});
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  const lastPrice = priceHistory[0];
  const avgPrice = priceHistory.length
    ? (priceHistory.reduce((s, h) => s + parseFloat(h.purchase_price), 0) / priceHistory.length).toFixed(2)
    : null;

  return (
    <form onSubmit={handleSubmit} className="space-y-5">

      {/* Ürün seçimi */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Ürün Seç</label>
        <input
          type="text"
          placeholder="Ürün adı, offer_id veya SKU ara..."
          value={productSearch}
          onChange={(e) => setProductSearch(e.target.value)}
          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-blue-300"
        />
        <div className="border border-gray-200 rounded-lg overflow-hidden max-h-44 overflow-y-auto">
          {filteredProducts.map((p) => (
            <button
              key={p.offer_id}
              type="button"
              onClick={() => { set('offer_id', p.offer_id); setProductSearch(''); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-blue-50 transition-colors border-b border-gray-50 last:border-0 ${
                form.offer_id === p.offer_id ? 'bg-blue-50' : 'bg-white'
              }`}
            >
              {p.image_url ? (
                <img src={p.image_url} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0" />
              ) : (
                <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center text-gray-300 flex-shrink-0">📦</div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-800 truncate">{p.name || '—'}</div>
                <div className="text-xs text-gray-400 font-mono">{p.offer_id}</div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className={`text-xs font-semibold ${p.stock_quantity === 0 ? 'text-red-500' : 'text-green-600'}`}>
                  {p.stock_quantity} adet
                </div>
              </div>
            </button>
          ))}
          {!filteredProducts.length && (
            <div className="px-3 py-4 text-center text-sm text-gray-400">Ürün bulunamadı</div>
          )}
        </div>

        {/* Seçilen ürün + fiyat özeti */}
        {selected && (
          <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-blue-800 truncate">{selected.name}</div>
              <div className="text-xs text-blue-500 font-mono mt-0.5">{selected.offer_id}</div>
              <div className="text-xs text-blue-600 mt-1">Mevcut stok: <strong>{selected.stock_quantity}</strong></div>
            </div>
            {priceHistory.length > 0 && (
              <div className="text-right flex-shrink-0">
                <div className="text-xs text-blue-500 mb-1">Son {priceHistory.length} alım</div>
                <PriceChart history={priceHistory} />
                <div className="text-xs text-blue-600 mt-1">
                  Ort: <strong>{avgPrice} {lastPrice?.currency}</strong>
                  {lastPrice && <span className="ml-2">Son: <strong>{lastPrice.purchase_price} {lastPrice.currency}</strong></span>}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Miktar + Fiyat + Döviz */}
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1.5">Adet *</label>
          <input
            type="number"
            min="1"
            required
            value={form.quantity}
            onChange={(e) => set('quantity', e.target.value)}
            placeholder="0"
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 text-center font-semibold"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1.5">Alış Fiyatı *</label>
          <input
            type="number"
            min="0"
            step="0.01"
            required
            value={form.purchase_price}
            onChange={(e) => set('purchase_price', e.target.value)}
            placeholder="0.00"
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1.5">Döviz</label>
          <select
            value={form.currency}
            onChange={(e) => set('currency', e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
          >
            <option>TRY</option>
            <option>USD</option>
            <option>EUR</option>
            <option>RUB</option>
          </select>
        </div>
      </div>

      {/* Depo yeri */}
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Depo Yeri</label>
        <input
          type="text"
          list="locations-list"
          value={form.warehouse_location}
          onChange={(e) => set('warehouse_location', e.target.value)}
          placeholder="Örn: A-Raf-3, Depo-1, Mağaza"
          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
        />
        <datalist id="locations-list">
          {locations.map((l) => <option key={l} value={l} />)}
        </datalist>
      </div>

      {/* Tedarikçi */}
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Tedarikçi / Kaynak</label>
        <input
          type="text"
          value={form.supplier}
          onChange={(e) => set('supplier', e.target.value)}
          placeholder="Örn: Alibaba, Yurt içi tedarikçi..."
          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
        />
      </div>

      {/* Not */}
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Not</label>
        <textarea
          value={form.note}
          onChange={(e) => set('note', e.target.value)}
          rows={2}
          placeholder="İsteğe bağlı not..."
          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-300"
        />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg">{error}</div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 text-sm px-4 py-3 rounded-lg">
          ✓ <strong>{success.qty} adet</strong> eklendi. Yeni stok: <strong>{success.newStock}</strong>
        </div>
      )}

      <button
        type="submit"
        disabled={saving || !form.offer_id}
        className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold py-3 rounded-xl text-sm transition-colors"
      >
        {saving ? 'Kaydediliyor...' : '+ Stok Girişi Kaydet'}
      </button>
    </form>
  );
}

// ─── Geçmiş Tablosu ──────────────────────────────────────────────────────────

function EntryHistory({ entries, onDelete, loading }) {
  const [confirmId, setConfirmId] = useState(null);

  if (loading) return <div className="flex justify-center py-8"><Spinner /></div>;

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100">
        <h2 className="font-semibold text-gray-800">Son Stok Girişleri</h2>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100">
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Ürün</th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Adet</th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Alış Fiyatı</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Depo</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Tedarikçi</th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Tarih</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {entries.map((entry) => (
              <tr key={entry.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-800 truncate max-w-[200px]">{entry.product_name || '—'}</div>
                  <div className="font-mono text-xs text-gray-400 truncate max-w-[200px]">{entry.offer_id}</div>
                </td>
                <td className="px-4 py-3 text-center">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-green-50 text-green-700 font-bold text-sm">
                    +{entry.quantity}
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  <span className="font-semibold text-gray-800">{entry.purchase_price}</span>
                  <span className="text-xs text-gray-400 ml-1">{entry.currency}</span>
                </td>
                <td className="px-4 py-3 text-gray-600 text-xs">{entry.warehouse_location || '—'}</td>
                <td className="px-4 py-3 text-gray-600 text-xs truncate max-w-[140px]">{entry.supplier || '—'}</td>
                <td className="px-4 py-3 text-center text-xs text-gray-400 whitespace-nowrap">
                  {new Date(entry.created_at).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </td>
                <td className="px-4 py-3 text-center">
                  {confirmId === entry.id ? (
                    <div className="flex items-center gap-1">
                      <button onClick={() => { onDelete(entry.id); setConfirmId(null); }} className="text-xs text-red-600 font-medium hover:underline">Evet</button>
                      <span className="text-gray-300">/</span>
                      <button onClick={() => setConfirmId(null)} className="text-xs text-gray-400 hover:underline">İptal</button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirmId(entry.id)}
                      className="text-gray-300 hover:text-red-400 transition-colors text-xs"
                      title="Sil"
                    >
                      ✕
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!entries.length && (
              <tr>
                <td colSpan={7} className="text-center py-10 text-gray-400">Henüz stok girişi yok.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Ana Sayfa ────────────────────────────────────────────────────────────────

export default function StockEntry() {
  const [products, setProducts] = useState([]);
  const [entries, setEntries] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loadingEntries, setLoadingEntries] = useState(true);

  async function loadAll() {
    try {
      const [prods, ents, locs] = await Promise.all([
        api.getProducts(),
        apiFetch('/stock-entries'),
        apiFetch('/stock-entries/locations'),
      ]);
      setProducts(prods);
      setEntries(ents);
      setLocations(locs);
    } finally {
      setLoadingEntries(false);
    }
  }

  useEffect(() => { loadAll(); }, []);

  async function handleDelete(id) {
    try {
      await apiFetch(`/stock-entries/${id}`, { method: 'DELETE' });
      await loadAll();
    } catch (e) {
      alert('Silme hatası: ' + e.message);
    }
  }

  // Özet istatistikler
  const totalSpend = entries.reduce((s, e) => s + e.quantity * parseFloat(e.purchase_price), 0);
  const currencies = [...new Set(entries.map((e) => e.currency))];

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Stok Girişi</h1>
          <p className="text-sm text-gray-400 mt-0.5">Satın alma ve alış fiyatı takibi</p>
        </div>
        {entries.length > 0 && (
          <div className="text-right">
            <div className="text-xs text-gray-400">Toplam harcama</div>
            <div className="text-lg font-bold text-gray-800">
              {totalSpend.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} {currencies[0] || 'TRY'}
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        {/* Sol: Form */}
        <div className="bg-white border border-gray-200 rounded-2xl p-6">
          <h2 className="font-semibold text-gray-800 mb-5">Yeni Giriş</h2>
          <EntryForm products={products} locations={locations} onSaved={loadAll} />
        </div>

        {/* Sağ: Bilgi */}
        <div className="space-y-4">
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Nasıl Çalışır?</h3>
            <ul className="space-y-2 text-sm text-slate-600">
              <li className="flex gap-2"><span className="text-blue-500">①</span> Ürün seçin ve miktarı girin</li>
              <li className="flex gap-2"><span className="text-blue-500">②</span> Alış fiyatını ve dövizini girin</li>
              <li className="flex gap-2"><span className="text-blue-500">③</span> Depo yerini yazın (opsiyonel)</li>
              <li className="flex gap-2"><span className="text-blue-500">④</span> Kaydedin — stok otomatik güncellenir</li>
            </ul>
            <div className="mt-3 pt-3 border-t border-slate-200 text-xs text-slate-500">
              Aynı ürünü farklı fiyatlarla birden fazla kez girebilirsiniz. Fiyat geçmişi analiz için saklanır.
            </div>
          </div>
        </div>
      </div>

      {/* Geçmiş tablosu */}
      <div className="mt-8">
        <EntryHistory entries={entries} onDelete={handleDelete} loading={loadingEntries} />
      </div>
    </div>
  );
}
